import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { FileCheck, Send, ChevronRight, FileText } from "lucide-react";
import SLABadge from "../components/SLABadge";
import api, { API_URL } from "../lib/api";
import { toast } from "sonner";

export default function CaptureScreen() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [newJobs, setNewJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [models, setModels] = useState([]);
  const [stores, setStores] = useState([]);
  const [areas, setAreas] = useState([]);
  const [form, setForm] = useState({ model: "", store: "", area: "", job_type: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);

  // Load the selected job's PDF as a blob URL — see CollapsiblePdfPreview
  // for the same-origin-blob rationale (works inside the Hub iframe).
  useEffect(() => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfError(null);
    if (!selectedJob?.pdf_url) return;
    let cancelled = false;
    let createdUrl = null;
    api
      .get(`/files/${selectedJob.pdf_url}`, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(resp.data);
        setPdfBlobUrl(createdUrl);
      })
      .catch((e) => {
        if (cancelled) return;
        setPdfError(e.response?.data?.detail || "Failed to load PDF");
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob?.pdf_url]);

  useEffect(() => {
    loadData();
  }, [jobId]);

  const loadData = async () => {
    try {
      const [jobsRes, modelsRes, storesRes, areasRes] = await Promise.all([
        api.get("/job-cards?status=NEW&created_by_role=SS"),
        api.get("/models"),
        api.get("/stores"),
        api.get("/areas"),
      ]);
      setNewJobs(jobsRes.data.items || []);
      setModels(modelsRes.data || []);
      setStores(storesRes.data || []);
      setAreas(areasRes.data || []);

      if (jobId) {
        const { data } = await api.get(`/job-cards/${jobId}`);
        setSelectedJob(data);
      } else if (jobsRes.data.items?.length > 0) {
        setSelectedJob(jobsRes.data.items[0]);
      }
    } catch (err) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  const handleCapture = async () => {
    if (!selectedJob || !form.model || !form.store || !form.area || !form.job_type) {
      toast.error("Please fill all required fields including Delivery or Collection");
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/job-cards/${selectedJob.id}/capture`, form);
      toast.success("Job card captured and allocated!");
      setForm({ model: "", store: "", area: "", job_type: "", notes: "" });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to capture");
    }
    setSubmitting(false);
  };

  const selectJob = (job) => {
    setSelectedJob(job);
    setForm({ model: "", store: "", area: "", job_type: "", notes: "" });
  };

  const areaCollector = areas.find(a => a.area_name === form.area);

  if (loading) return <div className="text-slate-500 p-8">Loading...</div>;

  return (
    <div data-testid="capture-screen" className="space-y-4">
      <div>
        <h1 className="font-heading font-bold text-2xl tracking-tight text-slate-900 flex items-center gap-2">
          <FileCheck size={24} className="text-teal-600" /> Capture & Allocate
        </h1>
        <p className="text-slate-500 text-sm mt-1">Review PDF and capture job card details</p>
      </div>

      {/* Job Selector - horizontal scroll */}
      {newJobs.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {newJobs.map((job) => (
            <button
              key={job.id}
              data-testid={`select-job-${job.id}`}
              onClick={() => selectJob(job)}
              className={`shrink-0 px-4 py-2 rounded-xl border text-xs font-medium transition-all
                ${selectedJob?.id === job.id
                  ? "bg-teal-500/10 border-teal-500 text-teal-600"
                  : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"}`}
            >
              {job.job_card_number}
            </button>
          ))}
        </div>
      )}

      {newJobs.length === 0 && !selectedJob && (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <FileText size={40} className="mx-auto text-slate-400 mb-3" />
          <p className="text-slate-500">No new job cards to capture</p>
          <p className="text-slate-400 text-xs mt-1">Job cards need to be uploaded by Skyworth first</p>
        </div>
      )}

      {selectedJob && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* LEFT: PDF Viewer */}
          <div className="pdf-viewer flex flex-col">
            <div className="flex items-center justify-between p-3 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <FileText size={14} className="text-teal-600" />
                <span className="text-xs font-medium text-slate-700">{selectedJob.job_card_number}</span>
              </div>
              <SLABadge sla={selectedJob.sla} />
            </div>
            <div className="flex-1 min-h-[500px] flex items-center justify-center">
              {selectedJob.pdf_url ? (
                pdfError ? (
                  <div className="text-center px-6">
                    <FileText size={48} className="text-amber-500 mx-auto mb-2" />
                    <p className="text-slate-700 text-sm font-medium">PDF preview failed</p>
                    <p className="text-xs text-slate-400 mt-1">{pdfError}</p>
                    <a
                      href={`${API_URL}/api/files/${selectedJob.pdf_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium rounded-lg"
                    >
                      Open in new tab
                    </a>
                  </div>
                ) : pdfBlobUrl ? (
                  <iframe
                    data-testid="pdf-viewer"
                    src={`${pdfBlobUrl}#view=FitH`}
                    className="w-full h-full min-h-[500px]"
                    title="Job Card PDF"
                  />
                ) : (
                  <div className="text-center text-slate-400 text-sm">Loading PDF…</div>
                )
              ) : (
                <div className="text-center">
                  <FileText size={48} className="text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">No PDF attached</p>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Capture Form */}
          <div className="bg-white rounded-xl shadow-sm p-5 space-y-5">
            <div>
              <h2 className="font-heading font-bold text-lg tracking-tight">Capture Details</h2>
              <p className="text-slate-400 text-xs mt-0.5">JC: {selectedJob.job_card_number}</p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Model *</Label>
                <Select value={form.model} onValueChange={(v) => setForm({ ...form, model: v })}>
                  <SelectTrigger data-testid="capture-model" className="bg-slate-100 border-slate-300">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {models.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="capture-model-manual"
                  placeholder="Or type custom model"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="bg-slate-100 border-slate-300 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Store *</Label>
                <Select value={form.store} onValueChange={(v) => setForm({ ...form, store: v })}>
                  <SelectTrigger data-testid="capture-store" className="bg-slate-100 border-slate-300">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  data-testid="capture-store-manual"
                  placeholder="Or type custom store"
                  value={form.store}
                  onChange={(e) => setForm({ ...form, store: e.target.value })}
                  className="bg-slate-100 border-slate-300 text-sm"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Area *</Label>
                <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                  <SelectTrigger data-testid="capture-area" className="bg-slate-100 border-slate-300">
                    <SelectValue placeholder="Select area" />
                  </SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => (
                      <SelectItem key={a.area_name} value={a.area_name}>{a.area_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {areaCollector && (
                <div className="bg-slate-50 border border-slate-300 rounded-xl p-3 space-y-1.5">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Auto-assigned 3PA Company</p>
                    {areaCollector.company_name ? (
                      <p data-testid="capture-company-name" className="text-sm text-teal-600 font-semibold">{areaCollector.company_name}</p>
                    ) : (
                      <p data-testid="capture-company-name" className="text-sm text-amber-600 font-medium">No company configured for this area's collector</p>
                    )}
                  </div>
                  <div className="pt-1 border-t border-slate-200">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Primary Collector</p>
                    <p className="text-xs text-slate-600">{areaCollector.assigned_collector}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">All users in this company will see the job card.</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Delivery or Collection *</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="capture-job-type">
                  <button
                    type="button"
                    data-testid="capture-job-type-delivery"
                    onClick={() => setForm({ ...form, job_type: "DELIVERY" })}
                    className={`h-11 rounded-xl text-sm font-medium border transition-colors ${
                      form.job_type === "DELIVERY"
                        ? "bg-teal-600 text-white border-teal-700 shadow-sm"
                        : "bg-white text-slate-600 border-slate-300 hover:border-teal-500"
                    }`}
                  >
                    Delivery
                  </button>
                  <button
                    type="button"
                    data-testid="capture-job-type-collection"
                    onClick={() => setForm({ ...form, job_type: "COLLECTION" })}
                    className={`h-11 rounded-xl text-sm font-medium border transition-colors ${
                      form.job_type === "COLLECTION"
                        ? "bg-teal-600 text-white border-teal-700 shadow-sm"
                        : "bg-white text-slate-600 border-slate-300 hover:border-teal-500"
                    }`}
                  >
                    Collection
                  </button>
                </div>
                {!form.job_type && (
                  <p className="text-[11px] text-amber-600">Select Delivery or Collection before allocating.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Notes</Label>
                <Textarea
                  data-testid="capture-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Add capture notes..."
                  className="bg-slate-100 border-slate-300 text-sm min-h-[80px]"
                />
              </div>
            </div>

            <Button
              data-testid="capture-allocate-btn"
              onClick={handleCapture}
              disabled={submitting || !form.model || !form.store || !form.area || !form.job_type}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold"
            >
              {submitting ? "Allocating..." : (
                <>
                  <Send size={16} className="mr-2" /> Allocate to 3PA
                  <ChevronRight size={16} className="ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
